"use client";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Link from "next/link";
import { FcGoogle } from "react-icons/fc";
import {
  Eye,
  EyeOff,
  VerifiedIcon,
  UserCheck,
  UserPlus,
  BookOpen,
  ArrowRight,
  X,
} from "lucide-react";
import { LoginSchema } from "@/api/validation/auth-validation";
import { useLogin } from "@/api/hook/useAuth";
import { useGoogleAuth } from "@/api/hook/useGoogleAuth";
import { useCourseRegistration } from "@/api/hook/useRegistration";
import { getAuthToken } from "@/api/utils/session";
import { useQuery } from "@tanstack/react-query";
import {
  getCourseInfo,
  getMemberInfo,
  getUplineMemberNo,
} from "@/api/query/course";
import Header from "@/components/layout/Header";
import toast from "react-hot-toast";

interface CourseLoginClientProps {
  courseId: string;
  memberNo: string;
  initialCourseInfo?: any;
  initialMemberInfo?: any;
}

interface PrerequisiteError {
  message: string;
  courseId: string | number;
}

// ── Prerequisite Error Modal ──────────────────────────────────────────────────

function PrerequisiteModal({
  error,
  memberNo,
  onClose,
}: {
  error: PrerequisiteError;
  memberNo: string;
  onClose: () => void;
}) {
  const enrollUrl = `/course-registration/${memberNo}/${error.courseId}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000]/60 backdrop-blur-sm px-4">
      <div className="bg-[#FFF] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-[#1C1B4D] px-6 py-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FFF] flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-[#FFF] font-bold text-base">
                Prerequisite Required.
              </h2>
              <p className="text-[#FFF]/60 text-xs mt-0.5">
                Complete this course first to unlock enrollment.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#FFF]/50 hover:text-[#FFF] transition-colors mt-0.5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col gap-3 w-full">
          <p className="text-[#000] text-center text-sm leading-relaxed mb-6">
            {error.message}
          </p>

          <a
            href={enrollUrl}
            className="w-full text-center bg-primary hover:bg-primary/90 text-[#FFF] rounded-xl px-5 py-4 transition-all duration-200 group"
          >
            Click here to Enroll
          </a>

          <button
            onClick={onClose}
            className="w-full mt-3 text-[#000] hover:text-[#FFF] text-base py-2 transition-colors"
          >
            Go back
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Account Check Modal ───────────────────────────────────────────────────────

function AccountCheckModal({
  courseId,
  memberNo,
  onHasAccount,
  onNoAccount,
}: {
  courseId: string;
  memberNo: string;
  onHasAccount: () => void;
  onNoAccount: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-[#FFF] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-[#1C1B4D] px-6 py-6 text-center">
          <h2 className="text-[#FFF] text-base font-bold mb-1">
            Welcome to La Plage Meta Verse
          </h2>
          <p className="text-[#FFF] text-lg">
            Do you already have an account with us?
          </p>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <button
            onClick={onHasAccount}
            className="w-full flex items-center gap-4 bg-primary hover:bg-primary/90 text-[#FFF] rounded-xl px-5 py-4 transition-all duration-200"
          >
            <div className="w-10 h-10 rounded-full bg-[#FFF]/20 flex items-center justify-center shrink-0">
              <UserCheck className="w-5 h-5 text-[#FFF]" />
            </div>
            <div className="text-left">
              <p className="font-bold text-base">Yes, I have an account</p>
              <p className="text-[#FFF]/70 text-sm mt-0.5">
                Log in and enroll in this course
              </p>
            </div>
          </button>

          <button
            onClick={onNoAccount}
            className="w-full flex items-center gap-4 bg-[#FFF] border-2 border-primary hover:bg-primary/5 text-primary rounded-xl px-5 py-4 transition-all duration-200"
          >
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <UserPlus className="w-5 h-5 text-primary" />
            </div>
            <div className="text-left">
              <p className="font-bold text-base">No, I'm new here</p>
              <p className="text-primary/60 text-sm mt-0.5">
                Create an account and enroll
              </p>
            </div>
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 pb-5">
          Registration is completely free
        </p>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const CourseLoginClient = ({ courseId, memberNo }: CourseLoginClientProps) => {
  const router = useRouter();
  const loginMutation = useLogin(() => {});
  const { signInWithGoogle, isLoading: isGoogleLoading } = useGoogleAuth();
  const { mutateAsync: registerForCourse } = useCourseRegistration();

  const [showPassword, setShowPassword] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(true);
  const [prerequisiteError, setPrerequisiteError] =
    useState<PrerequisiteError | null>(null);

  const { data: uplineMemberNo } = useQuery({
    queryKey: ["uplineMemberNo", memberNo],
    queryFn: () => getUplineMemberNo(memberNo),
    enabled: memberNo?.startsWith("MKT"),
  });

  const { data: courseInfo } = useQuery({
    queryKey: ["courseInfo", courseId],
    queryFn: () => getCourseInfo(courseId),
  });

  const { data: memberInfo } = useQuery({
    queryKey: ["memberInfo", uplineMemberNo || memberNo],
    queryFn: () => getMemberInfo(uplineMemberNo || memberNo),
  });

  // ── Enrollment helper ─────────────────────────────────────────────────────

  const enrollAfterAuth = async (token: string): Promise<boolean> => {
    if (!courseId) return true;

    try {
      await registerForCourse({ courseId, memberNo, token });
      return true;
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const data = error.response.data;
        setPrerequisiteError({
          message:
            data?.message ??
            "You must complete a prerequisite course before enrolling.",
          courseId: data?.courseId ?? "",
        });
        return false;
      }

      // Already registered — treat as success and proceed to dashboard
      const errorData = error?.response?.data;
      const errorMessage =
        typeof errorData === "string" ? errorData : errorData?.message || "";
      if (errorMessage.toLowerCase().includes("already registered")) {
        toast.success("Welcome back! Redirecting to your dashboard...");
        return true;
      }

      throw error;
    }
  };

  // ── Google sign-in ────────────────────────────────────────────────────────
  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      const token = getAuthToken();
      if (token) {
        const canProceed = await enrollAfterAuth(token);
        if (!canProceed) return; // modal is already shown, stay on page
      }
      router.push("/dashboard");
    } catch (error) {
      console.error("Google sign in error:", error);
    }
  };

  // ── Email / password sign-in ──────────────────────────────────────────────
  const handleFormSubmit = async (values: {
    email: string;
    password: string;
  }) => {
    try {
      const authData = await loginMutation.mutateAsync(values);
      if (!authData?.token) return;

      const canProceed = await enrollAfterAuth(authData.token);
      if (!canProceed) return; // modal is already shown, stay on page

      router.push("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  // After - only show spinner if memberInfo is missing (needed for facilitator display)
  // courseInfo is optional; the form and enrollment still work without it
  if (!memberInfo) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <>
      <Header />

      {showAccountModal && (
        <AccountCheckModal
          courseId={courseId}
          memberNo={memberNo}
          onHasAccount={() => setShowAccountModal(false)}
          onNoAccount={() =>
            router.push(`/sign-up?courseId=${courseId}&memberNo=${memberNo}`)
          }
        />
      )}

      {prerequisiteError && (
        <PrerequisiteModal
          error={prerequisiteError}
          memberNo={memberNo}
          onClose={() => setPrerequisiteError(null)}
        />
      )}

      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Left Section */}
        <div className="hidden md:flex w-full lg:w-2/5 bg-[#1C1B4D] flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 py-12">
          <h1 className="font-bold text-[#FFF] text-2xl">
            {courseInfo?.courseName || "Start learning"} with
          </h1>
          <h1
            className="font-bold text-2xl mt-2 text-center lg:text-left"
            style={{
              backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              display: "inline-block",
            }}
          >
            La Plage Meta Verse
          </h1>

          <div className="mt-8 text-[#FFF]">
            <div className="mt-6 bg-[#FFF]/10 rounded-lg p-4">
              <p className="font-semibold text-xl mb-2">
                Facilitator: {memberInfo?.fullName}
              </p>
              {memberInfo?.verified && (
                <div className="flex items-center gap-2">
                  <span className="bg-[#486356] text-[#FFF] px-2 py-1 rounded-sm text-base">
                    Verified
                  </span>
                  <VerifiedIcon className="h-4 w-4 text-[#3bec97]" />
                </div>
              )}
            </div>

            {courseInfo?.shortVideoEnabled && courseInfo?.videoLink && (
              <div className="mt-6">
                <iframe
                  className="w-full aspect-video rounded-lg"
                  src={`https://www.youtube.com/embed/${getYoutubeVideoId(courseInfo.videoLink)}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Login Form */}
        <div className="w-full lg:w-3/5 flex justify-center items-center px-6 sm:px-10 py-12">
          <div className="w-full max-w-md">
            <h3 className="font-bold text-primary text-3xl sm:text-4xl text-center mb-8">
              Sign in to enroll
            </h3>

            <Formik
              initialValues={{ email: "", password: "" }}
              validationSchema={LoginSchema}
              onSubmit={handleFormSubmit}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form className="flex flex-col gap-6">
                  <div className="flex flex-col gap-3">
                    <label className="font-medium text-lg sm:text-xl text-primary">
                      Email Address
                    </label>
                    <Field
                      type="email"
                      name="email"
                      className={`rounded-md py-3 px-4 border ${
                        errors.email && touched.email
                          ? "border-red-500"
                          : "border-black"
                      }`}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>

                  <div className="flex flex-col gap-3">
                    <label className="font-medium text-lg sm:text-xl text-primary">
                      Password
                    </label>
                    <div className="relative">
                      <Field
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className={`rounded-md py-3 px-4 border w-full ${
                          errors.password && touched.password
                            ? "border-red-500"
                            : "border-black"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7280] hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>

                  <Link
                    href="/forgot-password"
                    className="font-bold text-lg sm:text-xl text-primary hover:text-primary/80"
                  >
                    Forgot Password
                  </Link>

                  <button
                    type="submit"
                    disabled={isSubmitting || loginMutation.isPending}
                    className="bg-primary rounded-md text-[#FFF] text-lg sm:text-xl font-bold py-3 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting || loginMutation.isPending
                      ? "Logging in...."
                      : "Log In"}
                  </button>

                  <p className="font-medium text-lg sm:text-xl text-center">
                    or
                  </p>

                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading || !courseInfo?.publish}
                    className="border rounded-md flex items-center py-3 px-4 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FcGoogle className="w-5 h-5" />
                    <p className="text-center flex-1">
                      {isGoogleLoading
                        ? "Connecting..."
                        : "Continue with Google"}
                    </p>
                  </button>

                  <p className="text-lg sm:text-xl text-center">
                    Don't have an account?{" "}
                    <Link
                      href={`/sign-up?courseId=${courseId}&memberNo=${memberNo}`}
                      className="font-medium text-primary hover:text-primary/80"
                    >
                      Sign up now!
                    </Link>
                  </p>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

function getYoutubeVideoId(url: string): string {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : "";
}

export default CourseLoginClient;
